import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ChatText, MagnifyingGlass, ArrowsClockwise, PaperPlaneTilt, Phone, Briefcase, Checks, Check, WarningCircle, Prohibit } from "@phosphor-icons/react";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "");
const STATUS_CLS = { delivered: "text-emerald-700 border-emerald-300", failed: "text-red-600 border-red-300", undelivered: "text-red-600 border-red-300", sent: "text-sky-700 border-sky-300" };

function ConversationRow({ c, active, onClick }) {
  return (
    <button type="button" onClick={onClick} data-testid={`sms-conversation-${c.phone.replace(/\D/g, "")}`}
      className={`w-full text-left px-3 py-2.5 border-b border-border hover:bg-muted/50 transition-colors ${active ? "bg-primary/10 border-l-2 border-l-primary" : ""}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium truncate">{c.contact || c.phone}</span>
        <span className="text-[10px] text-muted-foreground shrink-0">{fmt(c.last?.created_at)}</span>
      </div>
      <div className="flex items-center justify-between gap-2 mt-0.5">
        <p className="text-xs text-muted-foreground truncate">
          {c.last?.direction === "inbound" ? "" : "You: "}{c.last?.body}
        </p>
        {c.unread > 0 && <Badge className="h-4 px-1.5 text-[9px] bg-amber-500 hover:bg-amber-500 text-white shrink-0" data-testid="sms-unread-badge">{c.unread} new</Badge>}
      </div>
      <p className="text-[10px] text-muted-foreground mt-0.5 truncate">{c.contact ? `${c.phone} · ` : ""}{c.job?.reference ? `${c.job.reference} — ${c.job.title || ""}` : "No linked job"}</p>
    </button>
  );
}

function Bubble({ m }) {
  const inbound = m.direction === "inbound";
  return (
    <li className={`flex ${inbound ? "justify-start" : "justify-end"}`} data-testid={`sms-msg-${m.id}`}>
      <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-xs shadow-sm ${inbound ? "bg-muted rounded-bl-sm" : "bg-primary/10 dark:bg-primary/20 rounded-br-sm"}`}>
        <div className="flex items-center gap-2 text-[10px] text-muted-foreground mb-0.5">
          <span>{inbound ? "Client" : (m.by || "You")} · {fmt(m.created_at)}{!inbound && m.kind ? ` · ${m.kind.replace(/_/g, " ")}` : ""}</span>
          {inbound && m.read && m.replied_at && <span className="inline-flex items-center gap-0.5 text-emerald-600"><Checks size={12} /> replied</span>}
          {inbound && m.read && !m.replied_at && <span className="inline-flex items-center gap-0.5"><Check size={12} /> seen</span>}
        </div>
        <p className="whitespace-pre-wrap break-words">{m.body}</p>
        {m.error_message && <p className="text-red-600 mt-0.5">{m.error_message}</p>}
        {!inbound && <div className="text-right mt-0.5"><Badge variant="outline" className={`text-[9px] h-4 px-1 ${STATUS_CLS[m.status] || ""}`}>{m.status}</Badge></div>}
      </div>
    </li>
  );
}

export default function TextMessagesPage() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState("");
  const [unansweredOnly, setUnansweredOnly] = useState(params.get("filter") !== "all");
  const [data, setData] = useState({ items: [], unread: 0, connected: false });
  const [loading, setLoading] = useState(true);
  const [thread, setThread] = useState(null);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const listRef = useRef(null);
  const selected = params.get("phone") || "";

  const load = useCallback(async () => {
    try {
      const r = await API.get("/sms/conversations", { params: { q, unanswered: unansweredOnly, limit: 200 } });
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load text messages");
    } finally {
      setLoading(false);
    }
  }, [q, unansweredOnly]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  const openThread = useCallback(async (phone) => {
    if (!phone) { setThread(null); return; }
    try {
      const r = await API.get(`/sms/conversations/${encodeURIComponent(phone)}`);
      setThread(r.data);
      if (r.data.unread > 0) {
        await API.post(`/sms/conversations/${encodeURIComponent(phone)}/read`);
        load();
      }
      setTimeout(() => listRef.current?.scrollTo({ top: listRef.current.scrollHeight }), 50);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not open conversation");
    }
  }, [load]);

  useEffect(() => { openThread(selected); }, [selected, openThread]);

  const select = (phone) => { const n = new URLSearchParams(params); n.set("phone", phone); setParams(n, { replace: true }); };
  const toggleFilter = (v) => { setUnansweredOnly(v); const n = new URLSearchParams(params); v ? n.delete("filter") : n.set("filter", "all"); setParams(n, { replace: true }); };

  const send = async () => {
    if (!body.trim() || !thread) return;
    setSending(true);
    try {
      await API.post(`/sms/conversations/${encodeURIComponent(thread.phone)}/send`, { body: body.trim(), job_id: thread.job?.id || "" });
      setBody("");
      toast.success("Text sent");
      await openThread(thread.phone);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="text-messages-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><ChatText size={24} className="text-primary" /> Text Messages</h1>
          <p className="text-sm text-muted-foreground">Every SMS conversation with clients across all jobs — unanswered replies first.</p>
        </div>
        <div className="flex items-center gap-2">
          {data.unread > 0 && <Badge className="bg-amber-500 hover:bg-amber-500 text-white" data-testid="sms-total-unread">{data.unread} unanswered</Badge>}
          <Button variant="outline" size="sm" onClick={load} data-testid="sms-refresh-btn"><ArrowsClockwise size={14} className="mr-1" /> Refresh</Button>
        </div>
      </div>

      {!loading && !data.connected && (
        <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-700 p-3 text-xs text-amber-900 dark:text-amber-200 flex items-center gap-2" data-testid="sms-not-connected">
          <WarningCircle size={16} /> SMS isn't connected{data.connection_status ? ` (${data.connection_status})` : ""} — add your Twilio account under <Link to="/settings?tab=integrations" className="underline font-medium">Settings → Integrations</Link> to send and receive texts. Past conversations are still shown below.
        </div>
      )}

      <div className="grid lg:grid-cols-[360px_1fr] gap-4 min-h-[60vh]">
        <div className="bg-card border border-border rounded-lg overflow-hidden flex flex-col" data-testid="sms-conversation-list">
          <div className="p-3 border-b border-border space-y-2">
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-2.5 top-2.5 text-muted-foreground" />
              <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search number, name or job…" className="pl-8 h-9 text-sm" data-testid="sms-search-input" />
            </div>
            <div className="flex gap-1 text-xs">
              <button type="button" onClick={() => toggleFilter(true)} className={`px-2.5 h-7 rounded-md border ${unansweredOnly ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground"}`} data-testid="sms-filter-unanswered">Unanswered</button>
              <button type="button" onClick={() => toggleFilter(false)} className={`px-2.5 h-7 rounded-md border ${!unansweredOnly ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground"}`} data-testid="sms-filter-all">All</button>
            </div>
          </div>
          <div className="overflow-y-auto flex-1 max-h-[70vh]">
            {loading ? <p className="p-4 text-xs text-muted-foreground">Loading…</p>
              : data.items.length === 0 ? (
                <p className="p-6 text-sm text-muted-foreground text-center" data-testid="sms-empty">{unansweredOnly ? "No unanswered replies — you're all caught up." : "No text conversations yet. Texts sent from a job (reminders, on-the-way, replies) will appear here."}</p>
              ) : data.items.map(c => <ConversationRow key={c.phone} c={c} active={c.phone === selected} onClick={() => select(c.phone)} />)}
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg flex flex-col" data-testid="sms-thread-panel">
          {!thread ? (
            <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground p-8">Select a conversation to read and reply.</div>
          ) : (
            <>
              <div className="p-3 border-b border-border flex flex-wrap items-center justify-between gap-2">
                <div>
                  <p className="font-semibold text-sm flex items-center gap-2"><Phone size={14} className="text-muted-foreground" /> {thread.contact || thread.phone}{thread.contact && <span className="text-xs text-muted-foreground font-normal">{thread.phone}</span>}</p>
                  {thread.job && (
                    <Link to={`/jobs/${thread.job.id}#sms`} className="text-xs text-primary hover:underline inline-flex items-center gap-1 mt-0.5" data-testid="sms-thread-job-link">
                      <Briefcase size={12} /> {thread.job.reference} — {thread.job.title}
                    </Link>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {thread.opted_out && <Badge variant="outline" className="text-red-600 border-red-300 text-[10px]"><Prohibit size={11} className="mr-1" /> Opted out (STOP)</Badge>}
                  {thread.jobs?.length > 1 && <span className="text-[10px] text-muted-foreground">{thread.jobs.length} jobs</span>}
                </div>
              </div>
              <ul ref={listRef} className="flex-1 overflow-y-auto p-3 space-y-2 max-h-[55vh]" data-testid="sms-thread-messages">
                {thread.items.map(m => <Bubble key={m.id} m={m} />)}
              </ul>
              <div className="p-3 border-t border-border">
                <Textarea rows={2} value={body} onChange={e => setBody(e.target.value)} maxLength={480} placeholder={thread.opted_out ? "This number has opted out of texts" : "Type a reply…"} disabled={thread.opted_out || !data.connected} data-testid="sms-reply-input" />
                <div className="flex items-center justify-between mt-2">
                  <span className="text-[10px] text-muted-foreground">{body.length}/480{thread.job ? ` · logged on ${thread.job.reference}` : ""}</span>
                  <Button size="sm" onClick={send} disabled={sending || !body.trim() || thread.opted_out || !data.connected} data-testid="sms-reply-send">
                    <PaperPlaneTilt size={14} className="mr-1" /> {sending ? "Sending…" : "Send"}
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
